import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  LOCALE_ID,
  provideZoneChangeDetection,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { environment } from './environments/environment';
import { L10n, loadCldr, registerLicense } from '@syncfusion/ej2-base';
import * as Sentry from '@sentry/angular';
import type { Breadcrumb, ErrorEvent } from '@sentry/angular';

import numberingSystems from 'cldr-data/supplemental/numberingSystems.json';
import weekData from 'cldr-data/supplemental/weekData.json';

import svGregorian from 'cldr-data/main/sv/ca-gregorian.json';
import svNumbers from 'cldr-data/main/sv/numbers.json';
import svTimeZoneNames from 'cldr-data/main/sv/timeZoneNames.json';

import srGregorian from 'cldr-data/main/sr-Latn/ca-gregorian.json';
import srNumbers from 'cldr-data/main/sr-Latn/numbers.json';
import srTimeZoneNames from 'cldr-data/main/sr-Latn/timeZoneNames.json';

import isGregorian from 'cldr-data/main/is/ca-gregorian.json';
import isNumbers from 'cldr-data/main/is/numbers.json';
import isTimeZoneNames from 'cldr-data/main/is/timeZoneNames.json';
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import {
  ApiModule as AuthorityApiModule,
  Configuration as AuthorityConfiguration,
} from '@goatsports/authority/data-access';
import { ApiModule as CoreApiModule, Configuration as CoreConfiguration } from '@goatsports/core/data-access';
import { OAuthModule, OAuthStorage } from 'angular-oauth2-oidc';
import { TranslationsService, TranslocoRootModule } from '@goatsports/shared/util';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { AuthenticationService } from './app/shared/services/authentication.service';
import { ServerHttpInterceptor } from './app/shared/interceptors/server-http.interceptor';
import { ErrorService } from './app/shared/services/error.service';
import { ToastService } from './app/shared/services/toast.service';
import { AppVersionService } from './app/shared/services/app-version.service';
import { ConsentGateService } from './app/shared/services/consent-gate.service';
import { registerLocaleData } from '@angular/common';
import localeSv from '@angular/common/locales/sv';
import localeEn from '@angular/common/locales/en';
import localeSr from '@angular/common/locales/sr-Latn';
import localeIs from '@angular/common/locales/is';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { routes } from './app/app.routes';

registerLocaleData(localeSv);
registerLocaleData(localeEn);
registerLocaleData(localeSr);
registerLocaleData(localeIs);

loadCldr(
  numberingSystems,
  weekData,
  svGregorian,
  svNumbers,
  svTimeZoneNames,
  srGregorian,
  srNumbers,
  srTimeZoneNames,
  isGregorian,
  isNumbers,
  isTimeZoneNames,
);

L10n.load({
  en: {
    daterangepicker: {
      startLabel: 'Start date',
      endLabel: 'End date',
      applyText: 'Save',
      cancelText: 'Cancel',
      selectedDays: 'Selected days',
      days: 'days',
    },
    grid: {
      EmptyRecord: 'No records to display',
      GroupDropArea: 'Drag a column header here to group its column',
      UnGroup: 'Click here to ungroup',
      EmptyDataSourceError:
        'DataSource must not be empty at initial load since columns are generated from dataSource in AutoGenerate Column Grid',
      Item: 'item',
      Items: 'items',
      Expanded: 'Expanded',
      Collapsed: 'Collapsed',
      ChooseColumns: 'Choose Columns',
      Columnchooser: 'Columns',
      OKButton: 'OK',
      CancelButton: 'Cancel',
      SelectAll: 'Select All',
      Search: 'Search',
    },
    pager: {
      currentPageInfo: '{0} of {1} pages',
      totalItemsInfo: '({0} items)',
      totalItemInfo: '({0} item)',
      firstPageTooltip: 'Go to first page',
      lastPageTooltip: 'Go to last page',
      nextPageTooltip: 'Go to next page',
      previousPageTooltip: 'Go to previous page',
      nextPagerTooltip: 'Go to next pager items',
      previousPagerTooltip: 'Go to previous pager items',
      pagerDropDown: 'Items per page',
      pagerAllDropDown: 'Items',
    },
    'multi-select': {
      noRecordsTemplate: 'No Records Found',
      actionFailureTemplate: 'The Request Failed',
      overflowCountTemplate: '+${count} more..',
      totalCountTemplate: '${count} selected',
    },
    schedule: {
      day: 'Day',
      week: 'Week',
      workWeek: 'Work Week',
      month: 'Month',
      year: 'Year',
      today: 'Today',
    },
    datepicker: {
      today: 'Today',
    },
    calendar: {
      today: 'Today',
    },
    dropdowns: {
      noRecordsTemplate: 'No Records Found',
      actionFailureTemplate: 'The Request Failed',
      emptyResultText: 'No Results Found',
    },
    numerictextbox: {
      incrementTitle: 'Increment value',
      decrementTitle: 'Decrement value',
    },
    dialog: {
      close: 'Close',
    },
    /* uploader: {
          invalidMinFileSize: '',
          invalidMaxFileSize: '',
          invalidFileType: '',
          Browse: 'Browse',
          Clear: 'Clear',
          Upload: 'Upload',
          dropFilesHint: '',
          uploadFailedMessage: '',
          uploadSuccessMessage: '',
          removedSuccessMessage: '',
          removedFailedMessage: '',
          inProgress: '',
          readyToUploadMessage: '',
          remove: '',
          cancel: '',
          delete: '',
          totalFiles: '',
          size: '',
        }, */
  },
  sv: {
    daterangepicker: {
      startLabel: 'Startdatum',
      endLabel: 'Slutdatum',
      applyText: 'Spara',
      cancelText: 'Avbryt',
      selectedDays: 'Valda dagar',
      days: 'dagar',
    },
    grid: {
      EmptyRecord: 'Inga poster att visa',
      GroupDropArea: 'Dra en kolumnrubrik hit för att gruppera kolumnen',
      UnGroup: 'Klicka här för att avgruppera',
      EmptyDataSourceError:
        'Datakällan kan inte vara tom vid initial hämtning för att kolumnerna i datakällan används för att automatiskt genererande data kolumner',
      Item: 'rad',
      Items: 'rader',
      Expanded: 'Utökad',
      Collapsed: 'Kollapsade',
      ChooseColumns: 'Välj kolumner',
      Columnchooser: 'Kolumner',
      OKButton: 'OK',
      CancelButton: 'Avbryt',
      SelectAll: 'Välj alla',
      Search: 'Sök',
    },
    pager: {
      currentPageInfo: '{0} av {1} sidor',
      totalItemsInfo: '({0} rader)',
      totalItemInfo: '({0} rad)',
      firstPageTooltip: 'Gå till första sidan',
      lastPageTooltip: 'Gå till sista sidan',
      nextPageTooltip: 'Gå till nästa sida',
      previousPageTooltip: 'Gå till föregående sida',
      nextPagerTooltip: 'Gå till nästa sidas rader',
      previousPagerTooltip: 'Gå till föregående sidas rader',
      pagerDropDown: 'Rader per sida',
      pagerAllDropDown: 'Rader',
    },
    'multi-select': {
      noRecordsTemplate: 'Inga uppgifter funna',
      actionFailureTemplate: 'Action Failure Model',
      overflowCountTemplate: '+${count} mer..',
      totalCountTemplate: '${count} valda',
    },
    schedule: {
      day: 'Dag',
      week: 'Vecka',
      workWeek: 'Arbetsvecka',
      month: 'Månad',
      year: 'År',
      today: 'Idag',
    },
    datepicker: {
      today: 'Idag',
    },
    calendar: {
      today: 'Idag',
    },
    dropdowns: {
      noRecordsTemplate: 'Inga uppgifter funna',
      actionFailureTemplate: 'Begäran misslyckades',
      emptyResultText: 'Inga förslag',
    },
    numerictextbox: {
      incrementTitle: 'Öka värdet',
      decrementTitle: 'Minska värdet',
    },
    dialog: {
      close: 'Stäng',
    },
    uploader: {
      Browse: 'Bläddra',
      remove: 'Radera',
    },
  },
  'sr-Latn': {
    daterangepicker: {
      startLabel: 'Datum početka',
      endLabel: 'Datum završetka',
      applyText: 'Sačuvaj',
      cancelText: 'Otkaži',
      selectedDays: 'Izabrani dani',
      days: 'dani',
    },
    grid: {
      EmptyRecord: 'Nema zapisa za prikaz',
      GroupDropArea: 'Prevuci zaglavlje kolone ovde da grupišeš njegovu kolonu',
      UnGroup: 'Razgrupiši',
      EmptyDataSourceError:
        'DataSource ne sme biti prazan pri inicijalnom učitavanju jer se kolone generišu iz dataSource u AutoGenerate Column Grid',
      Item: 'stavka',
      Items: 'stavke',
      Expanded: 'Prošireno',
      Collapsed: 'Skupljeno',
      ChooseColumns: 'Izbor kolona',
      Columnchooser: 'Kolone',
      OKButton: 'OK',
      CancelButton: 'Otkaži',
      SelectAll: 'Izaberi sve',
      Search: 'Pretraži',
    },
    pager: {
      currentPageInfo: 'Strana {0} od {1}',
      totalItemsInfo: '(Broj stavki: {0})',
      totalItemInfo: '(Red: {0})',
      firstPageTooltip: 'Idi na prvu stranicu',
      lastPageTooltip: 'Idi na poslednju stranicu',
      nextPageTooltip: 'Idi na sledeću stranicu',
      previousPageTooltip: 'Idi na prethodnu stranicu',
      nextPagerTooltip: 'Vidi stavke na sledećoj stranici',
      previousPagerTooltip: 'Vidi stavke na prethodnoj stranici',
      pagerDropDown: 'Stavke po stranici',
      pagerAllDropDown: 'Stavke',
    },
    'multi-select': {
      noRecordsTemplate: 'Nema pronađenih zapisa',
      actionFailureTemplate: 'Zahtev nije uspešan',
      overflowCountTemplate: '+${count} više..',
      totalCountTemplate: '${count} odabrano',
    },
    schedule: {
      day: 'Dan',
      week: 'Nedelja',
      workWeek: 'Radna nedelja',
      month: 'Mesec',
      year: 'Godina',
      today: 'Danas',
    },
    datepicker: {
      today: 'Danas',
    },
    calendar: {
      today: 'Danas',
    },
    dropdowns: {
      noRecordsTemplate: 'Nema pronađenih zapisa',
      actionFailureTemplate: 'Zahtev nije uspešan',
      emptyResultText: 'Nema pronađenih rezultata',
    },
    numerictextbox: {
      incrementTitle: 'Povećanje vrednosti',
      decrementTitle: 'Smanjenje vrednosti',
    },
    dialog: {
      close: 'Zatvori',
    },
    uploader: {
      Browse: 'Pregledaj',
      remove: 'Ukloni',
    },
  },
  is: {
    daterangepicker: {
      startLabel: 'Upphafsdagsetning',
      endLabel: 'Lokadagsetning',
      applyText: 'Vista',
      cancelText: 'Hætta við',
      selectedDays: 'Valdir dagar',
      days: 'dagar',
    },
    grid: {
      EmptyRecord: 'Engar skrár til að sýna',
      GroupDropArea: 'Dragðu dálkhaus hingað til að flokka dálkinn hans',
      UnGroup: 'Smelltu hér til að taka upp hóp',
      EmptyDataSourceError:
        'DataSource má ekki vera tómt við upphafshleðslu þar sem dálkar eru búnir til úr dataSource í AutoGenerate Column Grid',
      Item: 'röð',
      Items: 'raðir',
      Expanded: 'Stækkað',
      Collapsed: 'Hrunið saman',
      ChooseColumns: 'Dálkavalin',
      Columnchooser: 'Dálkar',
      OKButton: 'Í lagi',
      CancelButton: 'Hætta við',
      SelectAll: 'Velja allt',
      Search: 'Leita',
    },
    pager: {
      currentPageInfo: '{0} af {1} síðum',
      totalItemsInfo: '({0} röð)',
      totalItemInfo: '({0} raðir)',
      firstPageTooltip: 'Farðu á fyrstu síðu',
      lastPageTooltip: 'Farðu á síðustu síðu',
      nextPageTooltip: 'Farðu á næstu síðu',
      previousPageTooltip: 'Farðu á fyrri síðu',
      nextPagerTooltip: 'Farðu í næstu röð í síðuboði',
      previousPagerTooltip: 'Farðu í fyrri boðsendingaratriði',
      pagerDropDown: 'raðir á síðu',
    },
    'multi-select': {
      noRecordsTemplate: 'Engar færslur fundust',
      actionFailureTemplate: 'Beiðni mistókst',
      overflowCountTemplate: '+${count} í viðbót..',
      totalCountTemplate: '${count} valið',
    },
    schedule: {
      day: 'Dagur',
      week: 'Vika',
      workWeek: 'Vinnuvika',
      month: 'Mánuður',
      year: 'Ár',
      today: 'Í dag',
    },
    datepicker: {
      today: 'Í dag',
    },
    calendar: {
      today: 'Í dag',
    },
    dropdowns: {
      noRecordsTemplate: 'Engar færslur fundust',
      actionFailureTemplate: 'Beiðni mistókst',
      emptyResultText: 'Engin útkoma fannst',
    },
    numerictextbox: {
      incrementTitle: 'Auka verðmæti',
      decrementTitle: 'Lækka gildi',
    },
    dialog: {
      close: 'Loka',
    },
    uploader: {
      Browse: 'Skoðaðu',
      remove: 'Fjarlægja',
    },
  },
});

// Privacy scrubbers for the Sentry init below. Sentry is used strictly for
// error + performance diagnostics — we strip anything that could carry personal
// data before it leaves the browser: IP addresses, cookies, auth tokens,
// request/response bodies (messages, reflections, health & nutrition payloads),
// and query strings.
function scrubEvent(event: ErrorEvent): ErrorEvent {
  if (event.user) {
    // Keep only the pseudonymous id (§7.7g); strip direct identifiers.
    delete event.user.ip_address;
    delete event.user.email;
    delete event.user.username;
  }
  if (event.request) {
    delete event.request.cookies;
    delete event.request.data;
    delete event.request.query_string;
    if (event.request.url) {
      event.request.url = event.request.url.split('?')[0];
    }
    if (event.request.headers) {
      delete event.request.headers['Authorization'];
      delete event.request.headers['Cookie'];
    }
  }
  return event;
}

function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  if (breadcrumb.data) {
    // Strip query strings from any URL-bearing breadcrumb — xhr/fetch carry
    // `url`, navigation carries `from`/`to` — since they can hold tokens or PII.
    for (const key of ['url', 'from', 'to']) {
      if (typeof breadcrumb.data[key] === 'string') {
        breadcrumb.data[key] = breadcrumb.data[key].split('?')[0];
      }
    }
    // Drop any captured request/response bodies from network breadcrumbs.
    delete breadcrumb.data['body'];
    delete breadcrumb.data['request_body'];
    delete breadcrumb.data['response_body'];
  }
  return breadcrumb;
}

if (environment.sentryDSN) {
  Sentry.init({
    dsn: environment.sentryDSN,
    release: 'goatsports@1.38.0',
    dist: '1',
    environment: environment.sentryEnvironment,
    // Session Replay is intentionally NOT enabled — it captures DOM content
    // (messages, reflections, health & nutrition data) and is disallowed by our
    // privacy policy. Keep Sentry to errors + performance tracing only.
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0,
    tracePropagationTargets: ['localhost', environment.coreApiUrl, environment.authorityApiUrl],
    sendDefaultPii: false,
    beforeSend: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
  });
}

if (environment.production) {
  enableProdMode();
}

registerLicense('Ngo9BigBOggjGyl/VkV+XU9AclRDX3xKf0x/TGpQb19xflBPallYVBYiSV9jS3hTdUdgWHpdd3FcRWNdUk91XA==');

function oAuthStorageFactory(): OAuthStorage {
  return localStorage;
}

function configureAuth(authenticationService: AuthenticationService) {
  return () => authenticationService.configureAuth(environment.authorityApiUrl);
}

function configureTranslations(translationsService: TranslationsService) {
  return () => {
    translationsService.initializeTranslation();
  };
}

bootstrapApplication(AppComponent, {
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    importProvidersFrom(
      CoreApiModule.forRoot(
        () =>
          new CoreConfiguration({
            basePath: environment.coreApiUrl,
          }),
      ),
      AuthorityApiModule.forRoot(
        () =>
          new AuthorityConfiguration({
            basePath: environment.authorityApiUrl,
            withCredentials: true,
          }),
      ),
      OAuthModule.forRoot({
        resourceServer: {
          allowedUrls: [environment.coreApiUrl],
          sendAccessToken: true,
        },
      }),
      TranslocoRootModule,
    ),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideRouter(
      routes,
      // withPreloading(PreloadAllModules),
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
    ),
    provideZoneChangeDetection({
      eventCoalescing: true,
    }),
    provideAnimationsAsync(),
    { provide: OAuthStorage, useFactory: oAuthStorageFactory },
    {
      provide: LOCALE_ID,
      useFactory: () => localStorage.getItem('language') ?? 'en',
    },
    provideAppInitializer(() => {
      const initializerFn = configureAuth(inject(AuthenticationService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = configureTranslations(inject(TranslationsService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      void inject(AppVersionService).initialize();
    }),
    provideAppInitializer(() => {
      inject(ConsentGateService).initializeLifecycleChecks();
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServerHttpInterceptor,
      deps: [ErrorService, AuthenticationService, ToastService],
      multi: true,
    },
  ],
});
